import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "../../lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const hasDialogDescription = (children) => {
  const descriptionDisplayName = DialogPrimitive.Description.displayName

  return React.Children.toArray(children).some((child) => {
    if (!React.isValidElement(child)) return false

    const typeDisplayName =
      typeof child.type === "string" ? child.type : child.type?.displayName

    if (typeDisplayName === descriptionDisplayName) return true

    return hasDialogDescription(child.props?.children)
  })
}

const isToastElement = (target) => {
  if (!(target instanceof Element)) return false

  return Boolean(
    target.closest("[data-sonner-toaster]") ||
      target.closest("[data-sonner-toast]") ||
      target.closest("[data-sonner-toast-wrapper]") ||
      target.closest("[data-sonner-toast-close-button]") ||
      target.closest(".toaster")
  )
}

const isPointerInsideToaster = (event) => {
  if (typeof document === "undefined") return false
  const originalEvent = event?.detail?.originalEvent
  if (!originalEvent) return false
  const { clientX, clientY } = originalEvent
  if (typeof clientX !== "number" || typeof clientY !== "number") return false

  const toasters = Array.from(document.querySelectorAll("[data-sonner-toaster]"))
  return toasters.some((toaster) => {
    const rect = toaster.getBoundingClientRect()
    return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom
  })
}

const isToastInteraction = (event) => {
  const originalTarget = event?.detail?.originalEvent?.target
  if (isToastElement(event.target) || isToastElement(originalTarget)) return true
  return isPointerInsideToaster(event)
}

const DialogContent = React.forwardRef(
  (
    {
      className,
      children,
      onInteractOutside,
      onPointerDownOutside,
      onFocusOutside,
      ...props
    },
    ref
  ) => {
  const { t } = useTranslation()
  const contentProps = { ...props }

  // Radix warns when there is no description. Opt out explicitly only when needed.
  if (
    !hasDialogDescription(children) &&
    !Object.prototype.hasOwnProperty.call(contentProps, "aria-describedby")
  ) {
    contentProps["aria-describedby"] = undefined
  }

  const handleInteractOutside = (event) => {
    if (isToastInteraction(event)) {
      event.preventDefault()
      return
    }
    onInteractOutside?.(event)
  }

  const handlePointerDownOutside = (event) => {
    if (isToastInteraction(event)) {
      event.preventDefault()
      return
    }
    onPointerDownOutside?.(event)
  }

  const handleFocusOutside = (event) => {
    if (isToastInteraction(event)) {
      event.preventDefault()
      return
    }
    onFocusOutside?.(event)
  }

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        data-dialog-content=""
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          className
        )}
        onInteractOutside={handleInteractOutside}
        onPointerDownOutside={handlePointerDownOutside}
        onFocusOutside={handleFocusOutside}
        {...contentProps}
      >
        {children}
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">{t("button.Close")}</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
  }
)
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
